import _extends from "@babel/runtime/helpers/esm/extends";
import React from 'react';
import PropTypes from 'prop-types';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Typography from '@material-ui/core/Typography';
import showIf from '@sprinx/react-showif/showIf';
import { useRedirect } from '@sprinx/react-routes';
import AppBarActionIconButton from '../AppBarActionIconButton';
/**
 * Vseobecne pouzitelny komponent pro implementaci akci AppBaru.
 *
 * Dle `variant` prepina zobrazeni mezi zobrazenim jako MenuItem a tlacitkem.
 *
 * Dale poskytuje nastroje na custom renderovani obsahu.
 *
 * @component
 */

var AppBarAction = showIf(function AppBarAction(_ref) {
  var disableInset = _ref.disableInset,
      icon = _ref.icon,
      label = _ref.label,
      onClick = _ref.onClick,
      path = _ref.path,
      renderMenuItem = _ref.renderMenuItem,
      renderStandalone = _ref.renderStandalone,
      variant = _ref.variant;
  var setRedirect = useRedirect();

  var handleClick = onClick || function () {
    return setRedirect(path);
  };

  var provideProps = {
    label: label,
    icon: icon,
    onClick: handleClick,
    setRedirect: setRedirect
  };

  if (variant === 'standalone') {
    return renderStandalone ? renderStandalone(provideProps) : React.createElement("div", null, React.createElement(AppBarActionIconButton, {
      color: "inherit",
      iconColor: "inherit",
      icon: icon,
      label: label,
      onClick: handleClick
    }));
  }

  if (variant === 'menuItem') {
    return renderMenuItem ? renderMenuItem(_extends({}, provideProps, {
      disableInset: disableInset
    })) : React.createElement(MenuItem, {
      onClick: handleClick
    }, icon && React.createElement(ListItemIcon, null, icon), React.createElement(Typography, {
      variant: "inherit",
      noWrap: true
    }, label));
  }

  return null;
});
AppBarAction.displayName = 'AppBarAction';
AppBarAction.propTypes = {
  /**
   * Zameni odsazeni labelu v modu 'menuItem'.
   * Vhodne v pripade, ze mate pouze jednu polozku, ktera nema iconu.
   */
  disableInset: PropTypes.bool,

  /**
   * Element ikony pro standardizovane zobrazeni. Nemusi byt definovano pokud jsou definovany render
   * property.
   */
  icon: PropTypes.element,

  /**
   * Label pro standardizovane zobrazeni. Nemusi byt definovano pokud jsou definovany render
   * property.
   */
  label: PropTypes.string,

  /**
   * Handler kliknuti na polozku. Funkce ve formatu `() => void`
   */
  onClick: PropTypes.func,

  /**
   * Cesta pro presmerovani. K presmerovani dojde pokud neni definovan `onClick` handler.
   */
  path: PropTypes.string,

  /**
   * Vraci Node pro mod `menuItem`. Pokud neni definovano, bude se renderovat jako MenuItem.
   *
   * Format funkce: ({ label, icon, onClick, setRedirect, disableInset }) => Node
   *
   * `onClick` je parent onClick nebo setRedirect(path)
   */
  renderMenuItem: PropTypes.func,

  /**
   * Vraci Node pro mod `standalone`. Pokud neni definovano, bude se renderovat jako IconButton.
   * V takovem pripade musi byt definovany property `icon`, `label` a `path` nebo `onClick`
   *
   * Format funkce: ({ label, icon, onClick, setRedirect }) => Node
   *
   * `onClick` je parent onClick nebo setRedirect(path)
   */
  renderStandalone: PropTypes.func,

  /**
   * Podminene zobrazeni vysledku.
   *
   * Pokud je hodnota `false`, nebo vysledek volani funkce je `false`,
   * komponenta se nebude zobrazovat.
   *
   * Funkce ma format `(showIfContext) => bool`.
   */
  showIf: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.func]),

  /**
   * Variant zobrazeni.
   *
   * - `none` nezobrazuje se.
   * - `standalone` aplikuje se renderStandalone pokud je definovano, nebo se renderuje IconButton.
   * - `menuItem` aplikuje se renderMenuItem pokud je definovano, nebo se renderuje MenuItem.
   */
  variant: PropTypes.oneOf(['none', 'standalone', 'menuItem'])
};
AppBarAction.defaultProps = {
  disableInset: false,
  icon: null,
  label: null,
  onClick: null,
  path: null,
  renderMenuItem: null,
  renderStandalone: null,
  showIf: true,
  variant: 'none'
};
export default AppBarAction;